import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Plane, MapPin, TowerControl, TrainFront } from "lucide-react";import api from "../lib/api";

/* Indian Airports Platform — master directory (Phase 1).
   Tenant-ready: brand text comes from the API tenant config, never hard-coded.
   Supports ?q= and ?state= deep links from the homepage search. */

const TYPE_ORDER = ["international", "customs", "civil_enclave", "domestic", "udan"];

export default function IndiaAirports() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [type, setType] = useState("");
  const [state, setState] = useState(params.get("state") || "");
  const [states, setStates] = useState([]);

  useEffect(() => {
    api.get("/india/airports").then((r) => setData(r.data)).catch(() => setData({ items: [], counts: {}, total: 0 }));
    api.get("/india/airports/states").then((r) => setStates(r.data.states)).catch(() => {});
    document.title = "Indian Airports — Every Airport. Every Journey. | TravelerPulse";
  }, []);

  const items = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.items.filter((a) =>
      (!type || a.type === type) &&
      (!state || a.state === state) &&
      (!needle || [a.name, a.city, a.state, a.iata, a.icao].some((v) => (v || "").toLowerCase().includes(needle))));
  }, [data, q, type, state]);

  if (!data) return <div className="max-w-6xl mx-auto px-5 py-12 text-sm text-zinc-500">Loading airports…</div>;
  const t = data.tenant || {};
  const labels = data.type_labels || {};

  return (
    <div data-testid="india-airports-page">
      {/* Hero — TravelerPulse.pro-style masthead, warmer saffron shade for the directory */}
      <div className="relative border-b border-zinc-200">
        <img src="https://images.unsplash.com/photo-1507812984078-917a274065be?auto=format&fit=crop&w=2000&q=80" alt="Indian airport terminal" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-orange-50/95 via-white/60 to-white/95" />
        <div className="relative max-w-6xl mx-auto px-5 py-10 text-center">
          <div className="inline-flex items-center gap-3" data-testid="ia-brand">
            <span className="inline-flex p-2.5 rounded-2xl bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 text-white shadow-xl shrink-0"><TowerControl className="w-6 h-6 sm:w-8 sm:h-8" /></span>
            <h1 className="font-heading font-900 text-3xl sm:text-4xl lg:text-5xl leading-none tracking-tight">
              <span className="text-blue-800">Indian</span><span className="text-orange-500">Airports</span><span className="text-zinc-500 text-xl sm:text-2xl">.com</span><span className="text-zinc-400 text-xs sm:text-sm align-super">™</span>
            </h1>
          </div>
          <div className="mt-3 flex items-center justify-center gap-3">
            <span className="h-px w-8 sm:w-20 bg-zinc-500/60" />
            <p className="text-[11px] sm:text-sm font-800 uppercase tracking-[0.22em] text-zinc-800">Every Airport. Every Journey.</p>
            <span className="h-px w-8 sm:w-20 bg-zinc-500/60" />
          </div>
          <p className="mt-2 text-sm font-600 text-zinc-700 max-w-2xl mx-auto">One India Aviation Platform · Powered by <span className="font-800 text-blue-800">{t.powered_by}</span></p>
          <div className="mt-5 max-w-xl mx-auto relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ia-search-input"
              placeholder="Search airport, city, state, IATA or ICAO — e.g. COK"
              className="w-full border border-zinc-300 rounded-full pl-11 pr-4 py-3.5 text-sm bg-white shadow-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div className="mt-4 flex flex-wrap justify-center gap-2" data-testid="ia-type-filters">
            <FilterPill active={!type} onClick={() => setType("")} label={`All · ${data.total}`} testid="ia-type-all" />
            {TYPE_ORDER.map((v) => (data.counts[v] || 0) > 0 && (
              <FilterPill key={v} active={type === v} onClick={() => setType(type === v ? "" : v)}
                label={`${labels[v] || v} · ${data.counts[v]}`} testid={`ia-type-${v}`} />
            ))}
            <select value={state} onChange={(e) => setState(e.target.value)} data-testid="ia-state-select"
              className="border border-zinc-300 rounded-full px-3 py-1.5 text-xs font-700 bg-white">
              <option value="">All states</option>
              {states.map((s) => <option key={s.state} value={s.state}>{s.state} ({s.airports})</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-8">
        {items.length === 0 ? (
          <p className="text-sm text-zinc-500 py-10 text-center border border-dashed border-zinc-300 rounded-2xl" data-testid="ia-empty">No airports match — try a different search or filter.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ia-cards">
            {items.map((a) => (
              <Link key={a.airport_id} to={`/india/airports/${a.slug}`} data-testid={`ia-card-${a.iata}`}
                className="group bg-white border border-zinc-200 rounded-2xl overflow-hidden hover:border-blue-400 hover:-translate-y-0.5 transition-[transform,border-color]">
                <div className="relative h-36 overflow-hidden">
                  <img src={a.image_url} alt={a.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <span className="absolute top-2 left-2 bg-white/90 backdrop-blur text-zinc-900 font-mono font-800 text-sm rounded px-2 py-0.5">{a.iata}</span>
                  <span className="absolute top-2 right-2 bg-blue-700/90 text-white text-[10px] font-800 uppercase rounded px-1.5 py-0.5">{(data.type_labels || {})[a.type] || a.type}</span>
                </div>
                <div className="p-4">
                  <div className="font-heading font-800 leading-snug">{a.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.city}, {a.state} <span className="font-mono text-zinc-400">· {a.icao}</span></div>
                  {a.tagline && <p className="text-xs text-zinc-600 mt-1.5 line-clamp-2">{a.tagline}</p>}
                  <div className="mt-2 text-[11px] text-zinc-400 flex items-center gap-3">
                    {a.opened && <span>Opened {a.opened}</span>}
                    {a.runways && <span className="inline-flex items-center gap-1"><TowerControl className="w-3 h-3" /> {a.runways} runway{a.runways > 1 ? "s" : ""}</span>}
                    {a.has_metro && <span className="inline-flex items-center gap-1 text-emerald-600 font-700"><TrainFront className="w-3 h-3" /> Metro</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
        <p className="mt-8 text-xs text-zinc-400 flex items-center gap-1.5" data-testid="ia-footer-note">
          <Plane className="w-3.5 h-3.5" /> The Indian Airports Platform is being built airport by airport — profiles carry a source and last-verified date because airport information changes.
        </p>
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`rounded-full px-3.5 py-1.5 text-xs font-700 border transition-colors ${active ? "bg-blue-700 text-white border-blue-700" : "bg-white text-zinc-600 border-zinc-300 hover:border-blue-500"}`}>
      {label}
    </button>
  );
}
